import { Card, IconButton } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { server } from "../../server";
import {
    RiArrowLeftLine,
    RiCoinLine,
    RiUser3Fill,
    RiTimeLine,
    RiHistoryLine,
    RiStore3Fill,
    RiAuctionLine,
    RiArrowRightSLine
} from "react-icons/ri";

export default function BidDetails() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const [bidDetails, setBidDetails] = useState([]);
    const [shopDetails, setShopDetails] = useState(state?.shop || null);

    // Get shopId from state (handles multiple ways it might be passed)
    const shopId = state?.shop?._id || state?.shop?.id || state?.shopId;

    const getShopDetails = async () => {
        if (!shopId || shopDetails) return;
        try {
            const { data } = await axios.get(`${server}/shop/getById`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
                },
                params: {
                    shopId: shopId,
                },
            });
            setShopDetails(data?.data || data);
        } catch (e) {
            console.error("Failed to fetch shop info:", e);
        }
    };

    const getBidDetailsById = async () => {
        if (!shopId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios.get(`${server}/bid/getByShopId`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("bvmToken")}`,
                },
                params: {
                    shopId: shopId,
                },
            });
            const bids = data?.data || data;
            // The sample data has highest bid at the top, but let's ensure it's sorted by amount descending
            const sortedBids = Array.isArray(bids)
                ? [...bids].sort((a, b) => b.bidAmount - a.bidAmount)
                : [];
            setBidDetails(sortedBids);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to fetch bid details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getShopDetails();
        getBidDetailsById();
    }, [shopId]);

    // Calculate Highest Bid
    const highestBid = bidDetails.length > 0
        ? bidDetails[0].bidAmount
        : 0;

    return (
        <div className="p-4 lg:p-8 bg-[#f8fafc] min-h-screen">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <IconButton
                            onClick={() => navigate(-1)}
                            className="!bg-white !border !border-slate-200 !shadow-sm hover:!bg-slate-50 transition-all shadow-blue-500/5"
                        >
                            <RiArrowLeftLine className="text-slate-600" />
                        </IconButton>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-bold text-slate-800 tracking-tight leading-none text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-slate-700 to-slate-800">
                                    Bid History
                                </h1>
                            </div>
                            <p className="text-slate-500 text-[10px] font-semibold mt-2 flex items-center gap-1.5 uppercase tracking-[1.5px] bg-slate-100 w-fit px-2 py-0.5 rounded-md border border-slate-200/50">
                                <RiStore3Fill className="text-blue-500 text-sm" />
                                {shopDetails?.shopName || "Loading Shop..."}
                                <RiArrowRightSLine className="text-slate-300" />
                                <span className="text-blue-600">{shopDetails?.shopCode || "ID: " + shopId}</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="px-6 py-3 bg-white border border-slate-100 rounded-[24px] shadow-xl shadow-slate-200/40 flex flex-col justify-center min-w-[120px]">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest leading-none mb-1.5 text-center">Total Bids</p>
                            <p className="text-2xl font-semibold text-slate-800 leading-none text-center tabular-nums">{bidDetails.length}</p>
                        </div>
                        <div className="px-6 py-3 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-[24px] shadow-2xl shadow-blue-500/30 flex flex-col justify-center min-w-[140px] border border-blue-400/20">
                            <p className="text-[10px] font-semibold text-blue-100/70 uppercase tracking-widest leading-none mb-1.5 text-center">Highest Bid</p>
                            <div className="flex items-center justify-center gap-1">
                                <span className="text-blue-200 text-sm font-semibold">₹</span>
                                <p className="text-2xl font-semibold leading-none tabular-nums">{highestBid.toLocaleString()}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Card */}
                <Card className="overflow-hidden rounded-[32px] border border-slate-200/50 shadow-2xl shadow-slate-200/30 bg-white">
                    <div className="px-8 py-6 bg-gradient-to-r from-slate-50/80 to-white border-b border-slate-100 flex items-center justify-between">
                        <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[2.5px] flex items-center gap-2.5">
                            <div className="p-1.5 bg-blue-50 rounded-lg">
                                <RiHistoryLine className="text-blue-600" />
                            </div>
                            Live Auction Logs
                        </h3>
                        {bidDetails.length > 0 && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 text-[10px] font-semibold rounded-full uppercase tracking-wider border border-green-100 shadow-sm animate-pulse">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                </span>
                                Real-time Refresh
                            </div>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/40">
                                    <th className="px-8 py-5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest border-b border-slate-100">Rank</th>
                                    <th className="px-8 py-5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest border-b border-slate-100">Bidder</th>
                                    <th className="px-8 py-5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest border-b border-slate-100">Bid Amount</th>
                                    <th className="px-8 py-5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest border-b border-slate-100">Timestamp</th>
                                    <th className="px-8 py-5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest text-right border-b border-slate-100">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100/50">
                                {loading && bidDetails.length === 0 ? (
                                    [...Array(6)].map((_, i) => (
                                        <tr key={i}>
                                            <td colSpan={5} className="px-8 py-10">
                                                <div className="h-6 bg-slate-50 rounded-2xl animate-pulse w-full"></div>
                                            </td>
                                        </tr>
                                    ))
                                ) : bidDetails.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-8 py-32 text-center">
                                            <div className="flex flex-col items-center gap-6">
                                                <div className="w-24 h-24 bg-gradient-to-br from-slate-50 to-white rounded-full flex items-center justify-center border-2 border-dashed border-slate-200/50 shadow-inner">
                                                    <RiCoinLine className="text-5xl text-slate-200" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <p className="text-slate-900 font-extrabold text-xl tracking-tight uppercase">History is empty</p>
                                                    <p className="text-slate-400 text-sm font-medium">Be the first to place a bid on this shop.</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    bidDetails.map((bid, index) => (
                                        <tr key={bid.id || bid.guid} className="group hover:bg-slate-200/50 transition-all duration-300">
                                            <td className="px-8 py-6">
                                                <div className={`w-10 h-10 rounded-[14px] flex items-center justify-center text-sm font-semibold shadow-lg transition-all duration-300 group-hover:rotate-6 ${index === 0 ? "bg-amber-100 text-amber-700 ring-4 ring-amber-50 shadow-amber-200/50" :
                                                    index === 1 ? "bg-slate-100 text-slate-600 shadow-slate-200/50" :
                                                        index === 2 ? "bg-orange-50 text-orange-600 shadow-orange-100/50" :
                                                            "bg-white border border-slate-100 text-slate-300 shadow-sm"
                                                    }`}>
                                                    {index === 0 ? <RiAuctionLine className="text-amber-500 text-xl" /> : index + 1}
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border-2 border-white shadow-xl transition-all duration-300 group-hover:shadow-blue-200/50 ${index === 0 ? "bg-gradient-to-br from-blue-600 to-indigo-700 text-white" : "bg-white text-slate-400 shadow-slate-100"
                                                        }`}>
                                                        <RiUser3Fill className="text-xl" />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <p className="text-[13px] font-semibold text-slate-800 group-hover:text-blue-600 transition-colors uppercase tracking-tight">{bid.bidderName}</p>

                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col">
                                                    <div className="flex items-end gap-1 mb-0.5">
                                                        <span className="text-blue-600 text-xs font-semibold mb-1">₹</span>
                                                        <span className={`text-2xl font-semibold tabular-nums tracking-tighter ${index === 0 ? "text-blue-700" : "text-slate-900"}`}>
                                                            {bid.bidAmount.toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest">Confirmed Bid</p>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-slate-50 rounded-xl">
                                                        <RiTimeLine className="text-blue-500 text-lg" />
                                                    </div>
                                                    <div className="space-y-0.5">
                                                        <div className="flex items-center gap-1.5 text-slate-800 font-semibold text-sm tabular-nums">
                                                            {new Date(bid.biddedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">
                                                            {new Date(bid.biddedAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-[16px] text-[10px] font-semibold uppercase tracking-[1px] shadow-sm transition-all duration-300 ${index === 0
                                                    ? "bg-gradient-to-r from-green-500/10 to-green-600/10 text-green-700 border border-green-200/50 shadow-green-100"
                                                    : "bg-slate-50/50 text-slate-400 border border-slate-100 group-hover:border-slate-200"
                                                    }`}>
                                                    {index === 0 ? (
                                                        <>
                                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                                                            Highest
                                                        </>
                                                    ) : "Outbid"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>


                </Card>
            </div>
        </div>
    );
}